import { closest, toArray } from '@nrk/dh-basis-common'
import { debounce } from '../lib/utils'
import styles from './scratch-before-after.module.css'

export default class ScratchBeforeAfter extends HTMLElement {
  static get observedAttributes() {
    return ['lock']
  }

  pattern
  _revealed = false

  connectedCallback() {
    const debouncedUpdateCanvasWidth = debounce(this._updateCanvasWidth, { delay: 500 })

    if (this._isFullWidth() || this._isFeatureCover()) {
      const images = toArray(this.querySelectorAll('img'))
      images.forEach((image) => {
        image.setAttribute('sizes', '100vw')
      })
    }

    if ('PointerEvent' in window) {
      document.body.addEventListener('pointerdown', this._handlePointerDown)
      this.toggleButton.addEventListener('pointerdown', (e) => e.stopImmediatePropagation())
      this.scratchButton.addEventListener('pointerdown', (e) => e.stopImmediatePropagation())
    } else if ('ontouchstart' in document.documentElement) {
      document.body.addEventListener('touchstart', this._handlePointerDown)
      document.body.addEventListener('touchmove', this._preventScrollInOldTouchDevices, {
        passive: false,
      })
      this.toggleButton.addEventListener('touchstart', (e) => e.stopImmediatePropagation())
      this.scratchButton.addEventListener('touchstart', (e) => e.stopImmediatePropagation())
    } else {
      document.body.addEventListener('mousedown', this._handlePointerDown)
      this.toggleButton.addEventListener('mousedown', (e) => e.stopImmediatePropagation())
      this.scratchButton.addEventListener('mousedown', (e) => e.stopImmediatePropagation())
    }
    debouncedUpdateCanvasWidth()

    window.addEventListener('resize', debouncedUpdateCanvasWidth)
    this.querySelectorAll('img').forEach((img) =>
      img.addEventListener('load', debouncedUpdateCanvasWidth),
    )

    this.toggleButton.addEventListener('click', this._toggleLock)
    this.scratchButton.addEventListener('click', this._completeScratch)
    this.hintText.addEventListener('click', this._toggleLock)
  }

  disconnectedCallback() {
    this.scratchContainer.removeEventListener('pointerdown', this._handlePointerDown)
    this.toggleButton.removeEventListener('click', this._toggleLock)
    this.scratchButton.removeEventListener('click', this._completeScratch)
    this.hintText.removeEventListener('click', this._toggleLock)
  }

  attributeChangedCallback(name) {
    switch (name) {
      case 'lock':
        this._upadateLock()
    }
  }

  _preventScrollInOldTouchDevices = (e) => {
    if (!('PointerEvent' in window) && this.mode === 'paint') {
      e.preventDefault()
    }
  }

  _upadateLock() {
    if (this.lock) {
      this.scratchContainer.style.setProperty('touch-action', 'none')
    } else {
      this.scratchContainer.style.setProperty('touch-action', 'pan-y')
    }
  }

  _updateCanvasWidth = async () => {
    const canvas = this.visibleCanvas
    const images = this.images
    if (
      images.some((image) => !image.complete) ||
      canvas.offsetWidth === this._currentCanvasWidth
    ) {
      return
    }

    const dpr = window.devicePixelRatio
    canvas.width = canvas.offsetWidth * dpr
    canvas.height = canvas.offsetHeight * dpr
    this.visibleContext = canvas.getContext('2d')

    const img = this.revealElement.querySelector('img')
    const intrinsicSize = await getIntrinsicSize(img)
    this.visibleContext.drawImage(
      img,
      0,
      0,
      intrinsicSize.width,
      intrinsicSize.height,
      0,
      0,
      canvas.width,
      canvas.height,
    )

    this.pattern = this.visibleContext.createPattern(this.visibleCanvas, 'no-repeat')
    this.visibleContext.clearRect(0, 0, this.visibleCanvas.width, this.visibleCanvas.height)
    if (this._supportsMaskImage()) {
      const maskCanvas = this.maskCanvas
      maskCanvas.width = maskCanvas.offsetWidth
      maskCanvas.height = maskCanvas.offsetHeight
      this.maskContext = maskCanvas.getContext('2d')

      this.revealElement.style.webkitMaskImage = `url(${this.maskCanvas.toDataURL()})`
      this.revealElement.style.maskImage = `url(${this.maskCanvas.toDataURL()})`
    }

    this._currentCanvasWidth = canvas.offsetWidth
  }

  _isFullWidth() {
    const remoterendered = closest(this, '.remoterenderedcontent-reference')
    return remoterendered && remoterendered.classList.contains('fc')
  }

  _isFeatureCover() {
    const cover = closest(this, '.article-feature__cover')
    return !!cover
  }

  _supportsMaskImage() {
    // eslint-disable-next-line compat/compat
    return (
      window?.CSS?.supports?.('mask-image: url("")') ||
      window?.CSS?.supports?.('-webkit-mask-image: url("")')
    )
  }

  _handlePointerDown = (e) => {
    const container = closest(e.target, `.${styles.scratchContainer}`)
    if (!container || !this.contains(container) || e.button === 2) {
      return
    }
    e.preventDefault()
    this.showHint = false
    this.toggleButton.style.pointerEvents = 'none'

    if ('PointerEvent' in window) {
      document.body.addEventListener('pointermove', this._handlePointerMove)
      document.body.addEventListener('pointerup', this._handlePointerUp)
      document.body.addEventListener('pointercancel', this._handlePointerCancel)
    } else if ('ontouchstart' in document.documentElement) {
      document.body.addEventListener('touchmove', this._handlePointerMove, { passive: false })
      document.body.addEventListener('touchend', this._handlePointerUp)
      document.body.addEventListener('touchcancel', this._handlePointerCancel)
    } else {
      document.body.addEventListener('mousemove', this._handlePointerMove)
      document.body.addEventListener('mouseup', this._handlePointerUp)
    }

    if (this._supportsMaskImage()) {
      this.visibleCanvas.style.transition = ''
      this.visibleCanvas.style.opacity = 1
      this.visibleContext.clearRect(0, 0, this.visibleCanvas.width, this.visibleCanvas.height)
    }

    this._handlePointerMove(e)
    this._handlePointerMove(e)
  }

  _handlePointerMove = (e) => {
    e.preventDefault()
    e.stopImmediatePropagation()
    const container = closest(e.target, `.${styles.scratchContainer}`)
    let offsetX
    let offsetY
    if (!container) {
      offsetX = -1
      offsetY = -1
    } else if ('PointerEvent' in window) {
      offsetX = e.offsetX
      offsetY = e.offsetY
    } else if ('ontouchstart' in document.documentElement) {
      offsetX = e.targetTouches[0].pageX - container.offsetLeft
      offsetY = e.targetTouches[0].pageY - container.offsetTop
    } else {
      offsetX = e.offsetX
      offsetY = e.offsetY
    }
    const dpr = window.devicePixelRatio
    this._drawLine(this.visibleContext, this.prevPos, { offsetX, offsetY }, this.pattern, dpr)
    this._drawLine(this.maskContext, this.prevPos, { offsetX, offsetY }, 'white', 1)
    this.prevPos = { offsetX, offsetY }
  }

  _drawLine(ctx, prevPos, nextPos, fill, dpr) {
    const innerWidth = Math.min(1200, window.innerWidth)
    const lineWidth = 40 + 0.04 * innerWidth
    const { width, height } = ctx.canvas

    ctx.save()
    ctx.strokeStyle = fill
    ctx.lineWidth = lineWidth * dpr
    ctx.lineCap = 'round'

    if (
      prevPos &&
      nextPos &&
      prevPos.offsetX > 0 &&
      prevPos.offsetX < width &&
      prevPos.offsetY > 0 &&
      prevPos.offsetY < height &&
      nextPos.offsetX > 0 &&
      nextPos.offsetX < width &&
      nextPos.offsetY > 0 &&
      nextPos.offsetY < height
    ) {
      ctx.beginPath()
      ctx.moveTo(prevPos.offsetX * dpr, prevPos.offsetY * dpr)
      ctx.lineTo(nextPos.offsetX * dpr, nextPos.offsetY * dpr)
      ctx.stroke()
    }
    ctx.restore()
  }

  _handlePointerOut = () => {
    delete this.prevPos
  }

  _handlePointerCancel = (e) => {
    this.showHint = true
    setTimeout(() => {
      this.showHint = false
    }, 10000)
    this._handlePointerUp(e)
  }

  _handlePointerUp = () => {
    delete this.prevPos

    if ('PointerEvent' in window) {
      document.body.removeEventListener('pointermove', this._handlePointerMove)
      document.body.removeEventListener('pointerup', this._handlePointerUp)
      document.body.removeEventListener('pointercancel', this._handlePointerCancel)
    } else if ('ontouchstart' in document.documentElement) {
      document.body.removeEventListener('touchmove', this._handlePointerMove, false)
      document.body.removeEventListener('touchend', this._handlePointerUp)
      document.body.removeEventListener('touchcancel', this._handlePointerCancel)
    } else {
      document.body.removeEventListener('mousemove', this._handlePointerMove)
      document.body.removeEventListener('mouseup', this._handlePointerUp)
    }

    if (this._supportsMaskImage()) {
      // Replace canvas with masked image
      this.revealElement.style.webkitMaskImage = `url(${this.maskCanvas.toDataURL()})`
      this.revealElement.style.maskImage = `url(${this.maskCanvas.toDataURL()})`
      this.visibleCanvas.style.transition = 'opacity 350ms ease-in-out 200ms'
      this.visibleCanvas.style.opacity = 0
      this.revealElement.style.opacity = 1
    }
    this.toggleButton.style.pointerEvents = 'auto'
    this._checkRevealed()
  }

  _checkRevealed() {
    if (this._revealed) {
      return
    }
    const maskCanvas = this.maskCanvas
    const imageData = this.maskContext.getImageData(0, 0, maskCanvas.width, maskCanvas.height)

    let total = 0
    let pixels = imageData.data.length / 4
    for (let i = 0; i < pixels; i++) {
      total += imageData.data[i * 4 + 3] // alpha
    }
    const average = total / pixels
    if (average > 128) {
      this._revealed = true
      this.beforeElement.setAttribute('aria-hidden', 'true')
      this.revealElement.setAttribute('aria-hidden', 'false')
    }
  }

  _toggleLock = () => {
    this.showHint = false
    this.lock = !this.lock
  }

  _completeScratch = () => {
    console.log('Completing scratch')
    this.maskContext.save()
    this.maskContext.fillStyle = 'white'
    console.log(this.maskCanvas.width, this.maskCanvas.height)
    this.maskContext.fillRect(0, 0, this.maskCanvas.width, this.maskCanvas.height)
    this.maskContext.restore()
    this.revealElement.style.webkitMaskImage = `url(${this.maskCanvas.toDataURL()})`
    this.revealElement.style.maskImage = `url(${this.maskCanvas.toDataURL()})`
    this.revealElement.style.opacity = 1
    this._checkRevealed()
    this.scratchButton.setAttribute('disabled', '')

    this.afterImage.focus()
  }

  get hintText() {
    return this.querySelector(`.${styles.hint} p`)
  }

  get toggleButton() {
    return this.querySelector('button[value="lock"]')
  }

  get scratchButton() {
    return this.querySelector('button[value="scratch"]')
  }

  get images() {
    /* eslint-disable compat/compat */
    return Array.from(this.querySelectorAll('img'))
  }

  get scratchContainer() {
    return this.querySelector(`.${styles.scratchContainer}`)
  }

  get visibleCanvas() {
    return this.querySelector(`.${styles.visibleCanvas}`)
  }

  get maskCanvas() {
    return this.querySelector(`.${styles.maskCanvas}`)
  }

  get beforeElement() {
    return this.querySelector(`.${styles.before}`)
  }

  get revealElement() {
    return this.querySelector(`.${styles.after}`)
  }

  get afterImage() {
    return this.querySelector(`.${styles.after} img`)
  }

  get lock() {
    return this.hasAttribute('lock')
  }

  set lock(val) {
    const lock = JSON.parse(val)
    if (lock) {
      this.setAttribute('lock', '')
    } else {
      this.removeAttribute('lock')
    }
  }

  set showHint(val) {
    if (val) {
      this.setAttribute('show-hint', '')
    } else {
      this.removeAttribute('show-hint')
    }
  }

  get showHint() {
    return this.hasAttribute('show-hint')
  }
}

/*
 * Can not get image intrinsic values directly from naturalWidth and naturalHeight due to
 * https://bugs.chromium.org/p/chromium/issues/detail?id=760612&q=naturalWidth&can=2
 *
 *
 * */
function getIntrinsicSize(img) {
  // eslint-disable-next-line compat/compat
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      resolve({ width: image.width, height: image.height })
    }
    image.onerror = reject
    image.src = img.currentSrc || img.src
  })
}
